// src/components/cycles/SeanceCatalog.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Empty,
  Input,
  List,
  Space,
  Tag,
  Typography,
  message,
  Tooltip,
} from "antd";
import {
  CalendarOutlined,
  DragOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { DRAG_TYPE_SEANCE } from "../Utils/Constants";
import Constants from "../Utils/Constants";


const { Text, Paragraph } = Typography;

const api = axios.create({
  baseURL: Constants.SERVER_URL,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export default function SeanceCatalog({ dragType = DRAG_TYPE_SEANCE }) {
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);

  async function load() {
    setLoading(true);

    try {
      const { data } = await api.get("/sessions");
      setList(Array.isArray(data) ? data : data?.items || []);
    } catch (error) {
      console.error(error);
      message.error("Impossible de charger le catalogue des séances.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function onDragStart(event, session) {
    const payload = JSON.stringify({
      id: session.id || session.sessionId || session.key,
      key: session.key || session.id,
      name: session.title || session.name,
      description: session.description || "",
      videoUrl: session.videoUrl || "",
      category: session.category,
    });

    event.dataTransfer.setData(dragType, payload);
    event.dataTransfer.setData("text/plain", payload);
    event.dataTransfer.effectAllowed = "copy";
  }

  const filtered = useMemo(() => {
    const search = q.trim().toLowerCase();

    if (!search) return list;

    return list.filter((session) => {
      const text = `${session.title || session.name || ""} ${
        session.description || ""
      } ${session.category || ""}`.toLowerCase();

      return text.includes(search);
    });
  }, [list, q]);

  return (
    <Card
      title={
        <Space>
          <CalendarOutlined />
          Catalogue des séances
        </Space>
      }
      extra={
        <Input.Search
          placeholder="Filtrer…"
          allowClear
          value={q}
          onChange={(event) => setQ(event.target.value)}
          style={{ width: 220 }}
        />
      }
      bodyStyle={{ padding: 12 }}
    >
      <List
        loading={loading}
        dataSource={filtered}
        locale={{ emptyText: <Empty description="Aucune séance" /> }}
        renderItem={(session) => (
          <List.Item style={{ padding: "8px 0" }}>
            <Card
              size="small"
              hoverable
              draggable
              onDragStart={(event) => onDragStart(event, session)}
              style={{
                width: "100%",
                borderRadius: 12,
                cursor: "grab",
              }}
              bodyStyle={{ padding: 12 }}
            >
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Space
                  align="center"
                  style={{
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Text strong ellipsis title={session.title || session.name}>
                    {session.title || session.name || "Séance sans nom"}
                  </Text>

                  <Tooltip title="Glisser vers le cycle">
                    <DragOutlined style={{ color: "#1677ff" }} />
                  </Tooltip>
                </Space>

                <Paragraph
                  type="secondary"
                  ellipsis={{ rows: 2 }}
                  style={{ marginBottom: 0 }}
                >
                  {session.description || "Aucune description"}
                </Paragraph>

                <Space wrap size={[6, 4]}>
                  {session.category && <Tag>{session.category}</Tag>}

                  {session.videoUrl && (
                    <Tag icon={<VideoCameraOutlined />} color="purple">
                      vidéo
                    </Tag>
                  )}
                </Space>
              </Space>
            </Card>
          </List.Item>
        )}
      />
    </Card>
  );
}